
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Trash, Pencil } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface YahrzeitTableProps {
  yahrzeits: any[];
  onDelete: (id: string) => void;
  onEdit: (yahrzeit: any) => void;
}

const YahrzeitTable: React.FC<YahrzeitTableProps> = ({ yahrzeits, onDelete, onEdit }) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="space-y-4">
        {yahrzeits.map((yahrzeit) => (
          <div key={yahrzeit.id} className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex justify-between items-start mb-2">
              <div className="font-medium text-lg">{yahrzeit.name}</div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(yahrzeit)}
                  className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                >
                  <Pencil className="h-4 w-4" />
                  <span className="sr-only">ערוך</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(yahrzeit.id)}
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash className="h-4 w-4" />
                  <span className="sr-only">מחק</span>
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">בן/בת:</span>{" "}
                {yahrzeit.father_name ? (
                  <span>
                    {yahrzeit.gender === 'female' ? 'בת' : 'בן'} {yahrzeit.father_name}
                  </span>
                ) : '-'}
              </div>
              
              <div>
                <span className="text-gray-500">תאריך לועזי:</span>{" "}
                {new Date(yahrzeit.gregorian_date).toLocaleDateString()}
              </div>
              
              <div>
                <span className="text-gray-500">תאריך עברי:</span>{" "}
                {(yahrzeit.hebrew_day && yahrzeit.hebrew_month)
                  ? `${yahrzeit.hebrew_day} ${yahrzeit.hebrew_month}`
                  : '-'}
              </div>
              
              {yahrzeit.notes && (
                <div className="col-span-2">
                  <span className="text-gray-500">הערות:</span>{" "}
                  {yahrzeit.notes}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Desktop version with table
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>שם</TableHead>
          <TableHead>בן/בת</TableHead>
          <TableHead>תאריך לועזי</TableHead>
          <TableHead>תאריך עברי</TableHead>
          <TableHead>הערות</TableHead>
          <TableHead className="text-right">פעולות</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {yahrzeits.map((yahrzeit) => (
          <TableRow key={yahrzeit.id}>
            <TableCell className="font-medium">{yahrzeit.name}</TableCell>
            <TableCell>
              {yahrzeit.father_name ? (
                <span>
                  {yahrzeit.gender === 'female' ? 'בת' : 'בן'} {yahrzeit.father_name}
                </span>
              ) : '-'}
            </TableCell>
            <TableCell>{new Date(yahrzeit.gregorian_date).toLocaleDateString()}</TableCell>
            <TableCell>
              {(yahrzeit.hebrew_day && yahrzeit.hebrew_month)
                ? `${yahrzeit.hebrew_day} ${yahrzeit.hebrew_month}`
                : '-'}
            </TableCell>
            <TableCell className="max-w-xs truncate">{yahrzeit.notes || '-'}</TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(yahrzeit)}
                  className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                >
                  <Pencil className="h-4 w-4" />
                  <span className="sr-only">ערוך</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(yahrzeit.id)}
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash className="h-4 w-4" />
                  <span className="sr-only">מחק</span>
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default YahrzeitTable;
